<template>
  <v-container class="optimization-setup-container px-6 pt-0 pb-3">
    <v-dialog
      v-model="remove_channel_dialog"
      max-width="460px"
      content-class="optimization-channel-remove-dialog"
    >
      <v-card>
        <v-card-title class="optimization_create_title">
          Remove Channel
        </v-card-title>
        <v-card-text class="optimization-channel-remove-dialog-text">
          Remove {{ pending_remove_channel }} from this optimization?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            outlined
            color="black"
            class="optimization_calculator_action"
            @click="close_remove_channel_dialog"
          >
            Cancel
          </v-btn>
          <v-btn
            outlined
            color="error"
            class="optimization_calculator_action"
            @click="confirm_remove_channel"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="add_forecast_set_dialog"
      max-width="1500"
      content-class="optimization-forecast-set-dialog"
    >
      <v-card>
        <v-card-title class="optimization_create_title">
          Add Optimization Set
        </v-card-title>
        <v-card-text class="ma-0 pa-3">
          <v-row class="ma-0 pa-0">
            <v-col
              v-for="level in forecast_set_data_levels"
              :key="level"
              cols="12"
              class="ma-0 pa-1 optimization-forecast-set-channel-section"
            >
              <div class="optimization-scheduler-label">{{ level }}</div>
              <v-select
                v-model="new_forecast_set_filters[level]"
                :items="get_available_forecast_set_filter_options(level)"
                class="optimization_calculator_action"
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" class="ma-0 pa-1">
              <v-row class="ma-0 pa-0 optimization-forecast-set-form">
                <v-col cols="12" class="pa-0 optimization-date-picker-row">
                  <div class="optimization-scheduler-label">Month Selection</div>
                  <v-select
                    v-model="new_forecast_set_month"
                    :items="forecast_set_month_options"
                    item-text="label"
                    item-value="value"
                    class="optimization_calculator_action optimization-forecast-month-select"
                    hide-details
                    @change="update_new_forecast_set_month"
                  ></v-select>
                  <div class="optimization-date-range-summary">
                    <div class="optimization-scheduler-label">Date Range Summary</div>
                    <div class="optimization-date-range-summary__panel">
                      <div class="optimization-date-range-summary__item">
                        <div class="optimization-date-range-summary__label">
                          Start Date
                        </div>
                        <div class="optimization-date-range-summary__value">
                          {{ new_forecast_set_date_range.start_date }}
                        </div>
                      </div>
                      <div class="optimization-date-range-summary__item">
                        <div class="optimization-date-range-summary__label">
                          End Date
                        </div>
                        <div class="optimization-date-range-summary__value">
                          {{ new_forecast_set_date_range.end_date }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="optimization-forecast-set-actions">
          <v-btn
            outlined
            color="black"
            class="optimization_calculator_action optimization-forecast-set-add-button"
            :disabled="!can_submit_new_forecast_set"
            @click="confirm_add_forecast_set"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row
      v-if="loading"
      class="optimization-loading-page ma-0 pa-0"
      justify="center"
      align="center"
    >
      <v-col cols="auto" class="ma-0 pa-0">
        <v-progress-circular
          size="100"
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>
    </v-row>
    <v-row v-else class="optimization-setup-layout ma-0 pa-0">
      <v-col
        cols="12"
        md="1"
        v-if="!loading"
        class="optimization-tabs-column optimization-tabs-rail ma-0 pa-0 pt-3 pr-3"
      >
        <div
          v-if="available_optimization_versions"
          class="optimization-rail-section"
        >
          <span class="optimization-rail-label">Data Version</span>
          <v-select
            class="optimization-version-select"
            :items="available_optimization_versions"
            item-text="version_label"
            item-value="version_value"
            v-model="selected_version"
            @change="switch_optimization_version"
            single-line
            :disabled="save_optimizations"
            hide-details
          />
          <div
            v-if="selected_version !== max_available_version"
            class="optimization-version-warning"
          >
            Older version. Changes save to latest.
          </div>
        </div>
        <span class="optimization-rail-label">Optimizations</span>
        <v-tabs
          v-model="selected_optimization_tab"
          vertical
          slider-color="black"
          class="optimization-version-tabs"
        >
          <v-tab
            v-for="opt in visible_optimizations"
            :key="opt.ID"
            :href="'#optimization-' + opt.ID"
            class="optimization-version-tab"
          >
            {{ opt.Name }}
          </v-tab>
        </v-tabs>
        <div class="optimization-tab-actions">
          <div class="optimization-tab-action">
            <v-menu
              v-model="add_optimization_menu"
              :close-on-content-click="false"
              :nudge-width="200"
              :disabled="!can_edit_forecast_sets || optimizations.length > 4"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: ontooltip }">
                    <v-btn
                      icon
                      elevation="0"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!can_edit_forecast_sets || optimizations.length > 4"
                    >
                      <v-icon color="black" v-on="ontooltip">mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <TooltipDescription
                    title="Add New Optimization"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
              </template>
              <v-card>
                <v-card-title class="optimization_create_title"
                  >Create Optimization</v-card-title
                >
                <v-card-text class="ma-0 pa-3">
                  <v-row class="ma-0 pa-0">
                    <v-col cols="12" class="ma-0 pa-0">
                      <v-text-field
                        v-model="new_optimization_title"
                        class="ma-0 pa-0 optimization_calculator_action"
                        label="Optimization Name"
                        :disabled="!can_edit_forecast_sets"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <span class="optimization_calculator_action">Risk Level</span>
                    </v-col>
                    <v-col cols="12" class="ma-0">
                      <v-radio-group
                        v-model="new_optimization_risk"
                        class="ma-0 pa-0 optimization_calculator_action"
                        :disabled="!can_edit_forecast_sets"
                      >
                        <v-radio value="low" label="Low Risk"></v-radio>
                        <v-radio value="mid" label="Mid Risk"></v-radio>
                        <v-radio value="high" label="High Risk"></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    outlined
                    class="optimization_calculator_action"
                    @click="
                      new_optimization_title = '';
                      new_optimization_risk = 'low';
                      add_optimization_menu = false;
                    "
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    outlined
                    class="optimization_calculator_action"
                    @click="
                      add_optimization_menu = false;
                      add_new_optimization();
                    "
                    :disabled="!can_edit_forecast_sets || new_optimization_title === ''"
                  >
                    Create
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
          <div class="optimization-tab-action">
            <v-tooltip top>
              <template v-slot:activator="{ on: ontooltip }">
                <v-btn
                  icon
                  elevation="0"
                  v-on="ontooltip"
                  @click="copy_optimization"
                  :disabled="!can_edit_forecast_sets || optimizations.length > 4"
                >
                  <v-icon color="black">mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <TooltipDescription
                title="Copy Current Optimization"
                desc_2=""
              ></TooltipDescription>
            </v-tooltip>
          </div>
          <div class="optimization-tab-action">
            <v-tooltip top>
              <template v-slot:activator="{ on: ontooltip }">
                <v-btn
                  icon
                  elevation="0"
                  v-on="ontooltip"
                  @click="delete_optimization"
                  :disabled="optimizations.length < 2"
                >
                  <v-icon color="black">mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <TooltipDescription
                title="Delete Current Optimization"
                desc_2=""
              ></TooltipDescription>
            </v-tooltip>
          </div>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="3"
        v-if="!loading"
        class="optimization-setup-column ma-0 pa-0"
      >
        <div class="ma-0 pa-3 optimization-setup-panel">
          <v-row class="ma-0 pa-0" align="center">
            <v-col cols="9" class="ma-0 pa-1">
              <v-text-field
                v-model="my_optimization_name"
                class="optimization_calculator_action"
                label="Optimization Name"
                hide-details
                :disabled="!can_edit_forecast_sets"
                @change="update_optimization_name"
              ></v-text-field>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="ma-0 pa-1">
              <v-menu
                v-model="optimization_setup_global_menu"
                :close-on-content-click="false"
                :nudge-width="200"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-tooltip top>
                    <template v-slot:activator="{ on: ontooltip }">
                      <v-btn icon elevation="0" v-on="ontooltip">
                        <v-icon color="black" v-bind="attrs" v-on="on"
                          >mdi-dots-vertical</v-icon
                        >
                      </v-btn>
                    </template>
                    <TooltipDescription
                      title="Optimization Settings"
                      desc_2=""
                    ></TooltipDescription>
                  </v-tooltip>
                </template>
                <v-card width="400px">
                  <v-card-title class="optimization_create_title"
                    >Optimization Settings</v-card-title
                  >
                  <v-card-text class="ma-0 pa-3">
                    <v-row class="ma-0 pa-0">
                      <v-col cols="12" class="ma-0 pa-0">
                        <span>All Investment Budget Shifts</span>
                      </v-col>
                      <v-col cols="6" class="ma-0 pa-3">
                        <v-text-field
                          v-model="all_min_change"
                          prefix="-"
                          suffix="%"
                          type="number"
                          class="ma-0 pa-0 optimization_calculator_action"
                          hide-details
                          :disabled="!can_edit_forecast_sets"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6" class="ma-0 pa-3">
                        <v-text-field
                          v-model="all_max_change"
                          prefix="+"
                          suffix="%"
                          type="number"
                          class="ma-0 pa-0 optimization_calculator_action"
                          hide-details
                          :disabled="!can_edit_forecast_sets"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" class="ma-0 pa-0 pt-6">
                        <span>Lock All Investments</span>
                      </v-col>
                      <v-col cols="auto" class="ma-0 pa-0">
                        <v-switch
                          v-model="all_locked"
                          prepend-icon="mdi-lock-open"
                          append-icon="mdi-lock"
                          :disabled="!can_edit_forecast_sets"
                        >
                        </v-switch>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      outlined
                      class="optimization_calculator_action"
                      @click="optimization_setup_global_menu = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      outlined
                      class="optimization_calculator_action"
                      :disabled="!can_edit_forecast_sets"
                      @click="
                        set_all_changes();
                        optimization_setup_global_menu = false;
                      "
                    >
                      Save
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="12" class="ma-0 pa-1">
              <v-row class="optimization-total-budget-row ma-0 pa-0" align="end">
                <v-col cols="12" lg="6" class="ma-0 pa-0 pr-lg-1">
                  <v-text-field
                    v-model="optimization_total_budget"
                    class="optimization_calculator_action optimization-total-budget-field"
                    label="Total Budget"
                    hide-details
                    type="number"
                    prefix="$"
                    :disabled="!can_edit_forecast_sets"
                    @focus="capture_total_budget_snapshot"
                    @change="commit_total_budget_change"
                    @keydown.enter="commit_total_budget_change"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" lg="6" class="ma-0 pa-0 pl-lg-1">
                  <div class="optimization-total-budget-toggle-row">
                    <v-btn-toggle
                      v-model="total_budget_apply_method"
                      mandatory
                      dense
                      class="optimization-total-budget-toggle"
                      :disabled="!can_edit_forecast_sets"
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            class="optimization-total-budget-toggle-tooltip"
                            :class="{ 'optimization-total-budget-toggle-tooltip--active': can_edit_forecast_sets && total_budget_apply_method === 'spend' }"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-btn
                              value="spend"
                              class="optimization-total-budget-toggle-button"
                              :disabled="!can_edit_forecast_sets"
                              @click.prevent.stop="switch_total_budget_apply_method('spend')"
                              @mousedown.prevent="switch_total_budget_apply_method('spend')"
                            >
                              Budget values
                            </v-btn>
                          </span>
                        </template>
                        <TooltipDescription
                          title="Budget values"
                          desc_2="Applies the total budget change to unlocked channel budget values."
                        ></TooltipDescription>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            class="optimization-total-budget-toggle-tooltip"
                            :class="{ 'optimization-total-budget-toggle-tooltip--active': can_edit_forecast_sets && total_budget_apply_method === 'percent' }"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-btn
                              value="percent"
                              class="optimization-total-budget-toggle-button"
                              :disabled="!can_edit_forecast_sets"
                              @click.prevent.stop="switch_total_budget_apply_method('percent')"
                              @mousedown.prevent="switch_total_budget_apply_method('percent')"
                            >
                              % change
                            </v-btn>
                          </span>
                        </template>
                        <TooltipDescription
                          title="% change"
                          desc_2="Keeps channel budget values unchanged and updates unlocked min/max percentage limits."
                        ></TooltipDescription>
                      </v-tooltip>
                    </v-btn-toggle>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          elevation="0"
                          class="optimization-total-budget-reset"
                          :disabled="!can_edit_forecast_sets || !loaded_budget_snapshot"
                          @click="reset_budget_to_loaded_snapshot"
                          v-on="on"
                        >
                          <v-icon color="black">mdi-backup-restore</v-icon>
                        </v-btn>
                      </template>
                      <TooltipDescription
                        title="Reset Budget Values"
                        desc_2=""
                      ></TooltipDescription>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-card
            class="optimization-channel-card ma-0 mt-3 pa-1"
            v-for="(channel, idx) in optimization_channels"
            :key="idx"
          >
            <div class="optimization-channel-card-layout">
              <div class="optimization-channel-card-fields">
                <v-row class="ma-0 pa-0" align="center">
                  <v-col cols="auto" class="ma-0 pa-1">
                    <v-icon :color="get_channel_color(channel['channel'], idx)">mdi-circle</v-icon>
                  </v-col>
                  <v-col cols="auto" class="ma-0 pa-1">
                    <span class="optimization_calculator_channel">{{
                      channel["channel"]
                    }}</span>
                  </v-col>
                </v-row>
                <v-row class="ma-0 pa-0" align="center">
                  <v-col cols="5" class="ma-0 pa-1">
                    <v-text-field
                      prefix="$"
                      v-model="channel['spend']"
                      class="ma-0 pa-0 optimization_calculator_action"
                      label=""
                      hide-details
                      :disabled="!can_edit_forecast_sets"
                      @change="spend_shifted(channel['channel'], idx)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3" class="ma-0 pa-1">
                    <v-text-field
                      prefix="-"
                      suffix="%"
                      v-model="channel['min_change']"
                      class="ma-0 pa-0 optimization_calculator_action"
                      label=""
                      hide-details
                      :disabled="!can_edit_forecast_sets"
                      @change="change_budget_limit"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3" class="ma-0 pa-1">
                    <v-text-field
                      prefix="+"
                      suffix="%"
                      v-model="channel['max_change']"
                      class="ma-0 pa-0 optimization_calculator_action"
                      label=""
                      hide-details
                      :disabled="!can_edit_forecast_sets"
                      @change="change_budget_limit"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
              <div class="optimization-channel-card-actions">
                <v-tooltip
                  top
                  v-if="
                    !Object.keys(selected_response_curve_build).includes(
                      channel['channel']
                    )
                  "
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="black"
                      @click="open_response_curve(channel['channel'], idx)"
                      v-on="on"
                      >mdi-eye</v-icon
                    >
                  </template>
                  <TooltipDescription
                    title="View Investment"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
                <v-tooltip top v-else>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="black"
                      @click="close_response_curve(channel['channel'])"
                      v-on="on"
                      >mdi-eye-off</v-icon
                    >
                  </template>
                  <TooltipDescription
                    title="Hide Investment"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
                <v-tooltip top v-if="channel['lock']">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="black"
                      :disabled="!can_edit_forecast_sets"
                      @click="
                        if (!can_edit_forecast_sets) return;
                        change_channel_lock(channel);
                      "
                      v-on="on"
                      >mdi-lock</v-icon
                    >
                  </template>
                  <TooltipDescription
                    title="Investment Locked"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
                <v-tooltip top v-if="!channel['lock']">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="black"
                      :disabled="!can_edit_forecast_sets"
                      @click="
                        if (!can_edit_forecast_sets) return;
                        change_channel_lock(channel);
                      "
                      v-on="on"
                      >mdi-lock-open-outline</v-icon
                    >
                  </template>
                  <TooltipDescription
                    title="Investment Unlocked"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="black"
                      :disabled="!can_edit_forecast_sets || optimization_channels.length <= 1"
                      @click="can_edit_forecast_sets && open_remove_channel_dialog(channel['channel'])"
                      v-on="on"
                    >
                      mdi-trash-can-outline
                    </v-icon>
                  </template>
                  <TooltipDescription
                    :title="!can_edit_forecast_sets ? 'Available in Optimization Setup' : optimization_channels.length <= 1 ? 'At least one channel is required' : 'Remove channel'"
                    desc_2=""
                  ></TooltipDescription>
                </v-tooltip>
              </div>
            </div>
          </v-card>
          <v-row
            class="optimization-add-forecast-set-row ma-0 pa-0 mt-3"
            justify="center"
          >
            <v-col cols="auto" class="ma-0 pa-0">
              <v-tooltip top>
                <template v-slot:activator="{ on: ontooltip }">
                  <v-btn
                    icon
                    elevation="0"
                    v-on="ontooltip"
                    :disabled="!can_add_forecast_set"
                    @click="open_add_forecast_set_dialog"
                  >
                    <v-icon color="black">mdi-plus</v-icon>
                  </v-btn>
                </template>
                <TooltipDescription
                  :title="add_forecast_set_tooltip"
                  desc_2=""
                ></TooltipDescription>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="8"
        v-if="!loading"
        class="optimization-graph-column ma-0 pa-0 pl-md-3 pt-3 pb-8"
      >
        <div class="optimization-graph-panel">
          <v-row class="optimization-response-chart-row ma-0 pa-0">
            <v-col
              cols="12"
              v-if="selected_response_curve.length > 0"
              class="optimization-response-chart-column ma-0 pa-0"
            >
              <OptimizationResponseCurveChart
                :chart-data="selected_response_curve"
                :colors="colors"
                :channel-colors="response_curve_channel_colors"
                @response-curve-point-select="handleResponseCurvePointSelect"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import {
  ALERT_TYPE_ERROR,
  ALERT_TYPE_WARNING,
  ALERT_GENERIC_MESSAGE,
} from "@/constants/constants";
import TooltipDescription from "@/components/Utility/TooltipDescription.vue";
import OptimizationResponseCurveChart from "@/components/Forecasting/ForecastingSetup/Charts/OptimizationResponseCurveChart.vue";
import {
  FORECASTING_CHANNEL_COLORS,
  buildForecastingChannelColorMap,
  getForecastingChannelColor,
  normalizeForecastingChannelName,
} from "./forecastingChannelColors";

import api_caller from "@/javascript/forecasting.js";

export default {
  name: "ForecastingOptimizationSetup",
  props: [
    "data_version",
    "data_levels",
    "project_date_range",
    "project_plan_id",
    "model_version",
    "optimizing",
    "optimization_data_updated",
    "response_curves_key",
    "active_forecasting_tab",
    "current_planning_stage",
    "project_filters",
    "planning_start_date",
    "planning_end_date",
  ],
  components: {
    TooltipDescription,
    OptimizationResponseCurveChart,
  },
  data: () => ({
    loading: true,

    add_optimization_menu: false,
    new_optimization_title: "",
    new_optimization_risk: "low",
    save_optimizations: false,
    add_forecast_set_dialog: false,
    new_forecast_set_filters: {},
    new_forecast_set_month: null,
    new_forecast_set_date_range: {
      start_date: null,
      end_date: null,
    },

    optimizations: [],
    optimization_channels: [],
    my_optimization: null,
    my_optimization_name: "",
    performanceData: null,
    optimization_setups: {},

    optimization_total_budget: null,
    total_budget_apply_method: "spend",
    total_budget_edit_snapshot: null,
    loaded_budget_snapshot: null,
    optimization_calculator: null,

    optimization_setup_global_menu: false,

    response_curve_optimization: false,
    response_curves: {},
    selected_response_curve: [],
    selected_response_curve_build: {},
    selected_response_curve_channel: "",
    selected_response_curve_spend: 0,
    selected_response_curve_volume: 0,
    selected_response_curve_roas: 0,
    selected_response_curve_shift: false,

    save_optimization_ids: [],
    available_optimization_versions: null,
    selected_version: null,
    setup_data_stale: false,
    remove_channel_dialog: false,
    pending_remove_channel: null,

    colors: FORECASTING_CHANNEL_COLORS.slice(),
    all_min_change: 0,
    all_max_change: 10,
    all_locked: false,
  }),
  mounted() {
    if (this.should_refresh_setup_data()) {
      this.reset_table();
    }
  },
  computed: {
    selected_optimization_tab: {
      get() {
        if (
          !this.my_optimization ||
          !this.visible_optimizations.some((opt) => {
            return String(opt.ID) === String(this.my_optimization);
          })
        ) {
          return null;
        }
        return "optimization-" + this.my_optimization;
      },
      set(value) {
        if (!value) {
          return;
        }

        const selectedId = String(value).replace("optimization-", "");
        const selectedOptimization = this.visible_optimizations.find((opt) => {
          return String(opt.ID) === selectedId;
        });

        if (selectedOptimization) {
          this.my_optimization = selectedOptimization.ID;
        }
      },
    },
    visible_optimizations() {
      return this.optimizations.filter((opt) => {
        return opt && opt.ID !== undefined && opt.ID !== null;
      });
    },
    max_available_version() {
      if (!this.available_optimization_versions || this.available_optimization_versions.length === 0) {
        return null;
      }

      return Math.max(...this.available_optimization_versions.map((x) => x.version_value));
    },
    forecast_set_month_options() {
      return [
        { label: "January", value: 0 },
        { label: "February", value: 1 },
        { label: "March", value: 2 },
        { label: "April", value: 3 },
        { label: "May", value: 4 },
        { label: "June", value: 5 },
        { label: "July", value: 6 },
        { label: "August", value: 7 },
        { label: "September", value: 8 },
        { label: "October", value: 9 },
        { label: "November", value: 10 },
        { label: "December", value: 11 },
      ];
    },
    response_curve_channel_colors() {
      return buildForecastingChannelColorMap(
        this.optimization_channels.map((channel) => channel["channel"])
      );
    },
    can_edit_forecast_sets() {
      return this.current_planning_stage === "optimization_setup";
    },
    valid_forecast_set_data_levels() {
      return ["channel", "format", "media_type"];
    },
    optimization_label_values() {
      const labels = new Set();
      for (let channel of this.optimization_channels) {
        if (channel && channel["channel"] !== undefined && channel["channel"] !== null) {
          labels.add(String(channel["channel"]));
        }
      }

      return Array.from(labels);
    },
    forecast_set_data_levels() {
      const filterLevels = this.project_filters && typeof this.project_filters === "object"
        ? Object.keys(this.project_filters).filter((level) => {
          return this.valid_forecast_set_data_levels.includes(level);
        })
        : [];
      let orderedLevels = [];

      if (
        this.data_levels &&
        Array.isArray(this.data_levels["selected_levels"]) &&
        this.data_levels["selected_levels"].length > 0
      ) {
        orderedLevels = this.data_levels["selected_levels"].filter((level) => {
          return this.valid_forecast_set_data_levels.includes(level);
        });
      } else if (
        this.data_levels &&
        Array.isArray(this.data_levels["data_levels"]) &&
        this.data_levels["data_levels"].length > 0
      ) {
        orderedLevels = this.data_levels["data_levels"].filter((level) => {
          return this.valid_forecast_set_data_levels.includes(level);
        });
      }

      const inferredLevels = this.infer_forecast_set_data_levels(filterLevels);

      if (filterLevels.length === 0) {
        return orderedLevels;
      }

      if (orderedLevels.length === 0) {
        return inferredLevels.length > 0 ? inferredLevels : filterLevels;
      }

      const selectedLevels = orderedLevels.filter((level) => filterLevels.includes(level));
      if (selectedLevels.length === 0) {
        return inferredLevels;
      }
      if (selectedLevels.length > 1) {
        return selectedLevels;
      }
      if (
        selectedLevels.length === 1 &&
        inferredLevels.length > selectedLevels.length &&
        inferredLevels.includes(selectedLevels[0])
      ) {
        return inferredLevels;
      }
      if (
        selectedLevels.length === 1 &&
        inferredLevels.length > 0 &&
        selectedLevels[0] !== inferredLevels[0] &&
        !this.filter_level_matches_optimization_labels(selectedLevels[0])
      ) {
        return inferredLevels;
      }

      return selectedLevels;
    },
    can_submit_new_forecast_set() {
      const hasSelections = this.can_add_forecast_set && this.forecast_set_data_levels.every((level) => {
        const selectedValue = this.new_forecast_set_filters[level];
        return selectedValue !== undefined && selectedValue !== null && selectedValue !== "";
      });
      if (!hasSelections) {
        return false;
      }
      if (this.forecast_set_data_levels.length > 1) {
        return !this.is_existing_forecast_set_tuple(
          this.get_selected_forecast_set_filter_values()
        );
      }
      return true;
    },
    can_add_forecast_set() {
      if (!this.can_edit_forecast_sets || this.forecast_set_data_levels.length === 0) {
        return false;
      }
      if (this.forecast_set_data_levels.length > 1) {
        return this.get_first_available_forecast_set_tuple() !== null;
      }

      return this.can_edit_forecast_sets &&
        this.forecast_set_data_levels.every((level) => {
          return this.get_available_forecast_set_filter_options(level).length > 0;
        });
    },
    add_forecast_set_tooltip() {
      if (!this.can_edit_forecast_sets) {
        return "Available in Optimization Setup";
      }
      if (this.forecast_set_data_levels.length === 0) {
        return "No data levels available";
      }
      if (!this.can_add_forecast_set) {
        return "All selected data-level values are already included";
      }
      return "Add Optimization Set";
    },
    forecast_set_start_date() {
      return this.project_date_range && this.project_date_range["start_date"]
        ? this.project_date_range["start_date"]
        : null;
    },
    forecast_set_end_date() {
      return this.project_date_range && this.project_date_range["end_date"]
        ? this.project_date_range["end_date"]
        : null;
    },
    forecast_set_min_start_date() {
      if (!this.project_date_range) {
        return null;
      }
      return this.project_date_range["min_start_date"] || this.project_date_range["start_date"];
    },
    forecast_set_max_end_date() {
      if (!this.project_date_range) {
        return null;
      }
      return this.project_date_range["max_end_date"] || this.project_date_range["end_date"];
    },
    forecast_plan_duration_days() {
      if (this.planning_start_date && this.planning_end_date) {
        return this.get_date_range_days(
          this.planning_start_date,
          this.planning_end_date
        );
      }

      return this.get_date_range_days(
        this.forecast_set_start_date,
        this.forecast_set_end_date
      );
    },
  },
  watch: {
    project_plan_id: function () {
      if (this.should_refresh_setup_data()) {
        this.reset_table();
      }
    },
    my_optimization: function () {
      const opt_setup = this.get_current_optimization_setup();
      if (opt_setup) {

        this.optimization_calculator = opt_setup["calculator"];
        this.optimization_channels = opt_setup["optimization"].filter((opt) => {
          return opt["version_id"] == this.selected_version;
        });
        this.hydrate_total_budget_from_setup(opt_setup);
        this.my_optimization_name = opt_setup["title"];

        let opt_channels = [];
        for (let chnl of Object.keys(this.selected_response_curve_build)) {
          opt_channels.push([
            chnl,
            (this.selected_response_curve_build[chnl][0] - 1) / 2,
          ]);
        }
        for (let chnl of opt_channels) {
          this.close_response_curve(chnl[0]);
        }
        if (opt_channels.length == 0 && this.optimization_channels.length > 0) {
          opt_channels.push([this.optimization_channels[0]["channel"], 0]);
        }

        for (let chnl of opt_channels) {
          this.open_response_curve(chnl[0], chnl[1]);
        }
      } else {
        this.loaded_budget_snapshot = null;
        this.total_budget_edit_snapshot = null;
      }
    },
    optimizing: async function () {
      if (!this.optimizing) {
        return;
      }

      if (this.save_optimization_ids.length > 0 || this.save_optimizations) {
        const saved = await this.save_optimization();
        if (!saved) {
          this.$emit("optimizationSetupFailed");
          return;
        }
      }

      await this.reset_table(false);
      this.$emit("optimizationSetupComplete", this.selected_version);
    },
    optimization_data_updated: async function () {
      if (!this.project_plan_id) {
        return;
      }

      this.setup_data_stale = true;
      if (this.is_setup_tab_active()) {
        await this.reset_table();
        this.setup_data_stale = false;
      }
    },
    response_curves_key: async function () {
      if (this.should_refresh_stale_setup_data()) {
        await this.reset_table();
        this.setup_data_stale = false;
        return;
      }

      if (this.should_refresh_setup_data()) {
        await this.refresh_setup_data(true);
      }
    },
    current_planning_stage: async function () {
      if (this.should_refresh_setup_data()) {
        await this.refresh_setup_data(true);
      }
    },
  },
  methods: {
    should_refresh_setup_data: function () {
      return this.current_planning_stage === "optimization_setup" && Boolean(this.project_plan_id);
    },
    should_refresh_stale_setup_data: function () {
      return this.setup_data_stale && this.is_setup_tab_active() && Boolean(this.project_plan_id);
    },
    is_setup_tab_active: function () {
      return this.active_forecasting_tab === "opt_setup_new";
    },
    refresh_setup_data: async function (prefer_cache = false) {
      if (prefer_cache && this.restore_cached_setup_data()) {
        return;
      }

      await this.reset_table();
    },
    restore_cached_setup_data: function () {
      if (
        !this.performanceData ||
        !this.response_curves ||
        Object.keys(this.response_curves).length === 0
      ) {
        return false;
      }

      this.selected_response_curve = [];
      this.selected_response_curve_build = {};
      this.assign_optimization(this.performanceData);
      return true;
    },
    get_current_optimization_setup: function () {
      if (!this.my_optimization) {
        return null;
      }

      return this.optimization_setups[this.my_optimization] || null;
    },
    normalize_channel_spend: function (value) {
      const spend = Number(value);
      return Number.isFinite(spend) ? spend : 0;
    },
    parse_total_budget_input: function (value) {
      const spend = Number(value);
      return Number.isFinite(spend) ? spend : null;
    },
    round_budget_total: function (value) {
      return Math.round((value + Number.EPSILON) * 100) / 100;
    },
    normalize_percentage_value: function (value) {
      const percentage = Number(value);
      return Number.isFinite(percentage) ? percentage : 0;
    },
    get_version_optimization_channels: function (optSetup) {
      if (!optSetup || !Array.isArray(optSetup["optimization"])) {
        return [];
      }

      return optSetup["optimization"].filter((opt) => {
        return opt["version_id"] == this.selected_version;
      });
    },
    calculate_total_channel_budget: function (optSetup) {
      const totalBudget = this.get_version_optimization_channels(optSetup).reduce(
        (total, channel) => {
          return total + this.normalize_channel_spend(channel["spend"]);
        },
        0
      );

      return this.round_budget_total(totalBudget);
    },
    sync_total_budget_from_channels: function (
      optSetup = null,
      markDirtyOnChange = false,
      optimizationId = this.my_optimization
    ) {
      const setup = optSetup || this.get_current_optimization_setup();
      if (!setup) {
        return;
      }

      const totalBudget = this.calculate_total_channel_budget(setup);
      const previousTotalBudget = this.normalize_channel_spend(setup["total_budget"]);
      this.optimization_total_budget = totalBudget;
      this.$set(setup, "total_budget", totalBudget);
      this.total_budget_edit_snapshot = null;

      if (
        markDirtyOnChange &&
        optimizationId !== undefined &&
        optimizationId !== null &&
        previousTotalBudget !== totalBudget
      ) {
        this.save_optimization_ids.push(optimizationId);
        this.save_optimizations = true;
      }
    },
    hydrate_total_budget_from_setup: function (optSetup) {
      if (!optSetup) {
        return;
      }

      const savedTotalBudget = this.normalize_channel_spend(optSetup["total_budget"]);
      const channelTotalBudget = this.calculate_total_channel_budget(optSetup);
      this.optimization_total_budget = savedTotalBudget || channelTotalBudget;
      if (!savedTotalBudget && channelTotalBudget) {
        this.$set(optSetup, "total_budget", channelTotalBudget);
      }
      this.total_budget_edit_snapshot = null;
      this.capture_loaded_budget_snapshot(optSetup);
    },
    capture_total_budget_snapshot: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        this.total_budget_edit_snapshot = null;
        return;
      }

      const versionChannels = this.get_version_optimization_channels(optSetup);
      this.total_budget_edit_snapshot = {
        total_budget: this.normalize_channel_spend(optSetup["total_budget"]),
        channels: versionChannels.map((channel) => {
          return {
            channel: channel["channel"],
            spend: this.normalize_channel_spend(channel["spend"]),
            min_change: this.normalize_percentage_value(channel["min_change"]),
            max_change: this.normalize_percentage_value(channel["max_change"]),
            lock: Boolean(channel["lock"]),
          };
        }),
      };
    },
    capture_loaded_budget_snapshot: function (optSetup) {
      if (!optSetup) {
        this.loaded_budget_snapshot = null;
        return;
      }

      const versionChannels = this.get_version_optimization_channels(optSetup);
      this.loaded_budget_snapshot = {
        total_budget: this.normalize_channel_spend(optSetup["total_budget"]),
        channels: versionChannels.map((channel) => {
          return {
            channel: channel["channel"],
            spend: this.normalize_channel_spend(channel["spend"]),
            min_change: this.normalize_percentage_value(channel["min_change"]),
            max_change: this.normalize_percentage_value(channel["max_change"]),
          };
        }),
      };
    },
    reset_budget_to_loaded_snapshot: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup || !this.loaded_budget_snapshot) {
        return;
      }

      const snapshotsByChannel = {};
      for (let channel of this.loaded_budget_snapshot.channels) {
        snapshotsByChannel[channel.channel] = channel;
      }

      const changedSpendChannels = [];
      let resetChangedBudgetValues =
        this.normalize_channel_spend(optSetup["total_budget"]) !==
        this.loaded_budget_snapshot.total_budget;
      for (let channel of this.get_version_optimization_channels(optSetup)) {
        const snapshot = snapshotsByChannel[channel["channel"]];
        if (!snapshot) {
          continue;
        }

        if (this.normalize_channel_spend(channel["spend"]) !== snapshot.spend) {
          changedSpendChannels.push(channel["channel"]);
        }
        if (
          this.normalize_channel_spend(channel["spend"]) !== snapshot.spend ||
          this.normalize_percentage_value(channel["min_change"]) !== snapshot.min_change ||
          this.normalize_percentage_value(channel["max_change"]) !== snapshot.max_change
        ) {
          resetChangedBudgetValues = true;
        }
        this.$set(channel, "spend", snapshot.spend);
        this.$set(channel, "min_change", snapshot.min_change);
        this.$set(channel, "max_change", snapshot.max_change);
      }

      this.$set(optSetup, "total_budget", this.loaded_budget_snapshot.total_budget);
      this.optimization_total_budget = this.loaded_budget_snapshot.total_budget;
      this.total_budget_edit_snapshot = null;
      this.refresh_response_curves_for_channels(changedSpendChannels);
      if (resetChangedBudgetValues) {
        this.mark_current_optimization_dirty();
      }
    },
    restore_total_budget_from_setup: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        this.optimization_total_budget = null;
        this.total_budget_edit_snapshot = null;
        return;
      }

      this.optimization_total_budget = this.normalize_channel_spend(optSetup["total_budget"]);
      this.total_budget_edit_snapshot = null;
    },
    mark_current_optimization_dirty: function () {
      this.save_optimization_ids.push(this.my_optimization);
      this.save_optimizations = true;
    },
    change_budget_limit: function () {
      this.total_budget_edit_snapshot = null;
      this.mark_current_optimization_dirty();
    },
    change_channel_lock: function (channel) {
      channel["lock"] = !channel["lock"];
      this.total_budget_edit_snapshot = null;
      this.mark_current_optimization_dirty();
    },
    validate_total_budget_change: function (enteredTotal, snapshot) {
      if (
        !snapshot ||
        !Array.isArray(snapshot.channels) ||
        !Number.isFinite(enteredTotal) ||
        enteredTotal < 0
      ) {
        return null;
      }

      const lockedTotal = snapshot.channels.reduce((total, channel) => {
        return channel.lock ? total + channel.spend : total;
      }, 0);
      const unlockedChannels = snapshot.channels.filter((channel) => {
        return !channel.lock;
      });
      const unlockedCurrentTotal = unlockedChannels.reduce((total, channel) => {
        return total + channel.spend;
      }, 0);
      const unlockedTargetTotal = enteredTotal - lockedTotal;

      if (
        unlockedChannels.length === 0 ||
        unlockedCurrentTotal <= 0 ||
        unlockedTargetTotal < 0
      ) {
        return null;
      }

      return {
        lockedTotal,
        unlockedChannels,
        unlockedCurrentTotal,
        unlockedTargetTotal,
        sharedPctChange: (unlockedTargetTotal / unlockedCurrentTotal - 1) * 100,
      };
    },
    commit_total_budget_change: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      const parsedTotalBudget = this.parse_total_budget_input(this.optimization_total_budget);
      const enteredTotal = parsedTotalBudget === null
        ? null
        : this.round_budget_total(parsedTotalBudget);
      if (!this.total_budget_edit_snapshot) {
        this.capture_total_budget_snapshot();
      }
      const snapshot = this.total_budget_edit_snapshot;

      if (
        enteredTotal !== null &&
        snapshot &&
        enteredTotal === this.round_budget_total(snapshot.total_budget)
      ) {
        this.restore_total_budget_from_setup();
        return;
      }

      const budgetChange = this.validate_total_budget_change(enteredTotal, snapshot);

      if (!budgetChange) {
        this.restore_total_budget_from_setup();
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      if (this.total_budget_apply_method === "percent") {
        this.apply_total_budget_percent_change(optSetup, enteredTotal, budgetChange);
      } else {
        this.apply_total_budget_spend_change(optSetup, enteredTotal, budgetChange);
      }

      this.$set(optSetup, "total_budget", enteredTotal);
      this.optimization_total_budget = enteredTotal;
      this.mark_current_optimization_dirty();
    },
    restore_total_budget_line_values_from_snapshot: function (optSetup, snapshot) {
      if (!optSetup || !snapshot || !Array.isArray(snapshot.channels)) {
        return [];
      }

      const snapshotsByChannel = {};
      for (let channelSnapshot of snapshot.channels) {
        snapshotsByChannel[channelSnapshot.channel] = channelSnapshot;
      }

      const changedSpendChannels = [];
      for (let channel of this.get_version_optimization_channels(optSetup)) {
        const snapshotChannel = snapshotsByChannel[channel["channel"]];
        if (!snapshotChannel) {
          continue;
        }

        if (this.normalize_channel_spend(channel["spend"]) !== snapshotChannel.spend) {
          changedSpendChannels.push(channel["channel"]);
        }
        this.$set(channel, "spend", snapshotChannel.spend);
        this.$set(channel, "min_change", snapshotChannel.min_change);
        this.$set(channel, "max_change", snapshotChannel.max_change);
      }

      return changedSpendChannels;
    },
    switch_total_budget_apply_method: function (method) {
      if (!this.can_edit_forecast_sets || this.total_budget_apply_method === method) {
        return;
      }

      this.total_budget_apply_method = method;
      const optSetup = this.get_current_optimization_setup();
      const snapshot = this.total_budget_edit_snapshot;
      if (!optSetup || !snapshot) {
        return;
      }

      const parsedDisplayedTotal = this.parse_total_budget_input(this.optimization_total_budget);
      const currentTotalBudget = parsedDisplayedTotal === null
        ? this.normalize_channel_spend(optSetup["total_budget"])
        : this.round_budget_total(parsedDisplayedTotal);
      this.optimization_total_budget = currentTotalBudget;
      const budgetChange = this.validate_total_budget_change(currentTotalBudget, snapshot);
      if (!budgetChange) {
        this.total_budget_edit_snapshot = null;
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      const restoredSpendChannels = this.restore_total_budget_line_values_from_snapshot(
        optSetup,
        snapshot
      );
      if (method === "percent") {
        this.apply_total_budget_percent_change(optSetup, currentTotalBudget, budgetChange);
        this.refresh_response_curves_for_channels(restoredSpendChannels);
      } else {
        this.apply_total_budget_spend_change(optSetup, currentTotalBudget, budgetChange);
      }

      this.$set(optSetup, "total_budget", currentTotalBudget);
      this.optimization_total_budget = currentTotalBudget;
      this.mark_current_optimization_dirty();
    },
    apply_total_budget_spend_change: function (optSetup, enteredTotal, budgetChange) {
      const ratio = budgetChange.unlockedTargetTotal / budgetChange.unlockedCurrentTotal;
      const unlockedSnapshotsByChannel = {};
      let largestUnlockedChannel = null;
      let largestUnlockedSpend = -Infinity;

      for (let channel of budgetChange.unlockedChannels) {
        unlockedSnapshotsByChannel[channel.channel] = channel;
        if (channel.spend > largestUnlockedSpend) {
          largestUnlockedSpend = channel.spend;
          largestUnlockedChannel = channel.channel;
        }
      }

      const changedChannels = [];
      for (let channel of this.get_version_optimization_channels(optSetup)) {
        const snapshot = unlockedSnapshotsByChannel[channel["channel"]];
        if (!snapshot) {
          continue;
        }

        const newSpend = this.round_budget_total(snapshot.spend * ratio);
        this.$set(channel, "spend", newSpend);
        changedChannels.push(channel["channel"]);
      }

      const currentTotal = this.calculate_total_channel_budget(optSetup);
      const remainder = this.round_budget_total(enteredTotal - currentTotal);
      if (largestUnlockedChannel !== null && remainder !== 0) {
        for (let channel of this.get_version_optimization_channels(optSetup)) {
          if (channel["channel"] === largestUnlockedChannel) {
            this.$set(
              channel,
              "spend",
              this.round_budget_total(this.normalize_channel_spend(channel["spend"]) + remainder)
            );
            break;
          }
        }
      }

      this.refresh_response_curves_for_channels(changedChannels);
    },
    apply_total_budget_percent_change: function (optSetup, enteredTotal, budgetChange) {
      if (enteredTotal === this.round_budget_total(budgetChange.lockedTotal + budgetChange.unlockedCurrentTotal)) {
        return;
      }

      const unlockedSnapshotsByChannel = {};
      for (let channel of budgetChange.unlockedChannels) {
        unlockedSnapshotsByChannel[channel.channel] = channel;
      }

      const isIncrease = budgetChange.sharedPctChange > 0;
      const percentageAdjustment = Math.abs(budgetChange.sharedPctChange);
      for (let channel of this.get_version_optimization_channels(optSetup)) {
        const snapshot = unlockedSnapshotsByChannel[channel["channel"]];
        if (!snapshot) {
          continue;
        }

        if (isIncrease) {
          this.$set(
            channel,
            "max_change",
            this.round_budget_total(snapshot.max_change + percentageAdjustment)
          );
        } else {
          this.$set(
            channel,
            "min_change",
            this.round_budget_total(snapshot.min_change + percentageAdjustment)
          );
        }
      }
    },
    refresh_response_curves_for_channels: function (channels) {
      for (let channel of channels) {
        if (Object.keys(this.selected_response_curve_build).includes(channel)) {
          this.close_response_curve(channel);
          this.open_response_curve(channel);
        }
      }
    },
    update_optimization_name: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      optSetup["title"] = this.my_optimization_name;
      for (let idx = 0; idx < this.optimizations.length; idx++) {
        if (this.optimizations[idx] && this.optimizations[idx]["ID"] == this.my_optimization) {
          this.optimizations[idx]["Name"] = this.my_optimization_name;
        }
      }
      this.save_optimization_ids.push(this.my_optimization);
      this.save_optimizations = true;
    },
    get_channel_color: function (channel, index) {
      if (this.response_curve_channel_colors[channel]) {
        return this.response_curve_channel_colors[channel];
      }

      const normalizedChannel = normalizeForecastingChannelName(channel);
      if (this.response_curve_channel_colors[normalizedChannel]) {
        return this.response_curve_channel_colors[normalizedChannel];
      }

      return getForecastingChannelColor(channel, index);
    },
    open_remove_channel_dialog: function (channel) {
      if (this.optimization_channels.length <= 1) {
        return;
      }

      this.pending_remove_channel = channel;
      this.remove_channel_dialog = true;
    },
    close_remove_channel_dialog: function () {
      this.remove_channel_dialog = false;
      this.pending_remove_channel = null;
    },
    confirm_remove_channel: async function () {
      if (!this.pending_remove_channel || this.optimization_channels.length <= 1) {
        this.close_remove_channel_dialog();
        return;
      }

      const channel = this.pending_remove_channel;
      const optimizationId = this.my_optimization;
      const optSetup = this.get_current_optimization_setup();

      if (!optSetup) {
        this.close_remove_channel_dialog();
        return;
      }

      this.close_remove_channel_dialog();
      const deletedForecastSet = await this.delete_forecast_set({ channel });

      if (!deletedForecastSet) {
        return;
      }

      if (Object.keys(this.selected_response_curve_build).includes(channel)) {
        this.close_response_curve(channel);
      }
      if (this.selected_response_curve_channel === channel) {
        this.selected_response_curve_channel = "";
        this.selected_response_curve_spend = 0;
        this.selected_response_curve_volume = 0;
        this.selected_response_curve_roas = 0;
      }

      this.total_budget_edit_snapshot = null;
      this.optimization_channels = this.optimization_channels.filter((opt) => {
        return opt["channel"] !== channel;
      });
      optSetup["optimization"] = optSetup["optimization"].filter((opt) => {
        return !(
          opt["channel"] === channel &&
          opt["version_id"] == this.selected_version
        );
      });
      this.sync_total_budget_from_channels(optSetup, true, optimizationId);
    },
    set_all_changes() {
      let min_change = this.all_min_change;
      let max_change = this.all_max_change;
      let locked = this.all_locked;
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      for (let opt of optSetup["optimization"]) {
        opt["min_change"] = min_change;
        opt["max_change"] = max_change;
        opt["lock"] = locked;
      }
      this.total_budget_edit_snapshot = null;
      this.save_optimization_ids.push(this.my_optimization);
      this.save_optimizations = true;
    },
    update_opt_setup_vars: function () {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      this.sync_total_budget_from_channels(optSetup);
      optSetup["calculator"] = this.optimization_calculator;
      this.save_optimization_ids.push(this.my_optimization);
      this.save_optimizations = true;
    },
    normalize_filter_value: function (value) {
      return String(value).trim().toLowerCase();
    },
    split_optimization_label_value: function (value) {
      return String(value)
        .split(/\s+-\s+|\s+\|\s+/)
        .map((labelPart) => labelPart.trim())
        .filter((labelPart) => labelPart !== "");
    },
    filter_level_matches_optimization_labels: function (level) {
      if (this.optimization_label_values.length === 0) {
        return false;
      }

      const filterValueSet = new Set(
        this.get_project_filter_options(level).map((value) => {
          return this.normalize_filter_value(value);
        })
      );

      if (filterValueSet.size === 0) {
        return false;
      }

      return this.optimization_label_values.every((label) => {
        return filterValueSet.has(this.normalize_filter_value(label));
      });
    },
    infer_composite_forecast_set_data_levels: function (filterLevels) {
      if (this.optimization_label_values.length === 0) {
        return [];
      }

      const splitLabels = this.optimization_label_values.map((label) => {
        return this.split_optimization_label_value(label);
      });
      const levelCount = splitLabels[0] ? splitLabels[0].length : 0;
      if (
        levelCount <= 1 ||
        splitLabels.some((labelParts) => labelParts.length !== levelCount)
      ) {
        return [];
      }

      let inferredLevels = [];
      for (let idx = 0; idx < levelCount; idx++) {
        const matchingLevels = filterLevels.filter((level) => {
          if (inferredLevels.includes(level)) {
            return false;
          }
          const filterValueSet = new Set(
            this.get_project_filter_options(level).map((value) => {
              return this.normalize_filter_value(value);
            })
          );

          return splitLabels.every((labelParts) => {
            return filterValueSet.has(this.normalize_filter_value(labelParts[idx]));
          });
        });

        if (matchingLevels.length !== 1) {
          return [];
        }

        inferredLevels.push(matchingLevels[0]);
      }

      return inferredLevels;
    },
    infer_forecast_set_data_levels: function (filterLevels) {
      if (this.optimization_label_values.length === 0) {
        return [];
      }

      const compositeLevels = this.infer_composite_forecast_set_data_levels(filterLevels);
      if (compositeLevels.length > 0) {
        return compositeLevels;
      }

      for (let level of this.valid_forecast_set_data_levels) {
        if (
          filterLevels.includes(level) &&
          this.filter_level_matches_optimization_labels(level)
        ) {
          return [level];
        }
      }

      return [];
    },
    get_project_filter_options: function (level) {
      if (!this.project_filters || !Array.isArray(this.project_filters[level])) {
        return [];
      }

      return this.project_filters[level];
    },
    get_forecast_set_tuple_key: function (levels, valuesByLevel) {
      let values = [];
      for (let level of levels) {
        const selectedValue = valuesByLevel[level];
        if (selectedValue === undefined || selectedValue === null || selectedValue === "") {
          return null;
        }
        values.push(this.normalize_filter_value(selectedValue));
      }

      return values.join("||");
    },
    get_existing_optimization_tuple_set: function (levels) {
      let existingTuples = new Set();
      for (let label of this.optimization_label_values) {
        let valuesByLevel = {};

        if (levels.length === 1) {
          valuesByLevel[levels[0]] = label;
        } else {
          const labelParts = this.split_optimization_label_value(label);
          if (labelParts.length !== levels.length) {
            continue;
          }
          levels.forEach((level, idx) => {
            valuesByLevel[level] = labelParts[idx];
          });
        }

        const tupleKey = this.get_forecast_set_tuple_key(levels, valuesByLevel);
        if (tupleKey) {
          existingTuples.add(tupleKey);
        }
      }

      return existingTuples;
    },
    get_selected_forecast_set_filter_values: function (overrideLevel = null, overrideValue = null) {
      let selectedValues = {};
      for (let level of this.forecast_set_data_levels) {
        selectedValues[level] = level === overrideLevel
          ? overrideValue
          : this.new_forecast_set_filters[level];
      }

      return selectedValues;
    },
    is_existing_forecast_set_tuple: function (valuesByLevel) {
      const levels = this.forecast_set_data_levels;
      const tupleKey = this.get_forecast_set_tuple_key(levels, valuesByLevel);
      if (!tupleKey) {
        return false;
      }

      return this.get_existing_optimization_tuple_set(levels).has(tupleKey);
    },
    get_first_available_forecast_set_tuple: function () {
      const levels = this.forecast_set_data_levels;
      let selectedValues = {};

      const findTuple = (idx) => {
        if (idx >= levels.length) {
          return !this.is_existing_forecast_set_tuple(selectedValues);
        }

        const level = levels[idx];
        for (let option of this.get_project_filter_options(level)) {
          selectedValues[level] = option;
          if (findTuple(idx + 1)) {
            return true;
          }
        }
        delete selectedValues[level];
        return false;
      };

      return findTuple(0) ? { ...selectedValues } : null;
    },
    get_existing_optimization_value_set: function () {
      return new Set(
        this.optimization_label_values.map((value) => {
          return this.normalize_filter_value(value);
        })
      );
    },
    get_available_forecast_set_filter_options: function (level) {
      if (this.forecast_set_data_levels.length > 1) {
        return this.get_project_filter_options(level).filter((value) => {
          const selectedValues = this.get_selected_forecast_set_filter_values(level, value);
          return !this.is_existing_forecast_set_tuple(selectedValues);
        });
      }

      const existingOptimizationValueSet = this.get_existing_optimization_value_set();

      return this.get_project_filter_options(level).filter((value) => {
        return !existingOptimizationValueSet.has(this.normalize_filter_value(value));
      });
    },
    reset_new_forecast_set_form: function () {
      const startDate = this.forecast_set_min_start_date || this.forecast_set_start_date;
      this.new_forecast_set_month = startDate
        ? this.get_date_parts(startDate).month
        : 0;
      const resolvedStartDate = this.get_latest_forecast_set_month_start_date(
        this.new_forecast_set_month
      );
      this.new_forecast_set_filters = {};
      if (this.forecast_set_data_levels.length > 1) {
        const availableTuple = this.get_first_available_forecast_set_tuple() || {};
        for (let level of this.forecast_set_data_levels) {
          this.$set(this.new_forecast_set_filters, level, availableTuple[level] || null);
        }
      } else {
        for (let level of this.forecast_set_data_levels) {
          this.$set(
            this.new_forecast_set_filters,
            level,
            this.get_available_forecast_set_filter_options(level)[0] || null
          );
        }
      }
      this.new_forecast_set_date_range = {
        start_date: resolvedStartDate,
        end_date: this.get_forecast_set_end_date_from_start(resolvedStartDate),
      };
    },
    open_add_forecast_set_dialog: function () {
      if (!this.can_add_forecast_set) {
        return;
      }

      this.reset_new_forecast_set_form();
      this.add_forecast_set_dialog = true;
    },
    close_add_forecast_set_dialog: function () {
      this.add_forecast_set_dialog = false;
    },
    update_new_forecast_set_month: function (month) {
      const startDate = this.get_latest_forecast_set_month_start_date(month);
      this.new_forecast_set_date_range = {
        start_date: startDate,
        end_date: this.get_forecast_set_end_date_from_start(startDate),
      };
    },
    get_date_parts: function (dateValue) {
      if (!dateValue) {
        return {
          year: new Date().getFullYear(),
          month: 0,
          day: 1,
        };
      }

      const [year, month, day] = dateValue.split("-").map(Number);

      return {
        year,
        month: month - 1,
        day,
      };
    },
    format_date_parts: function (year, month, day) {
      return [
        year,
        String(month + 1).padStart(2, "0"),
        String(day).padStart(2, "0"),
      ].join("-");
    },
    format_date: function (date) {
      return date.toISOString().slice(0, 10);
    },
    get_latest_forecast_set_month_start_date: function (month) {
      const anchorDate =
        this.forecast_set_max_end_date ||
        this.forecast_set_end_date ||
        this.forecast_set_start_date;
      const maxEndDate = this.get_date_parts(anchorDate);
      const year =
        month <= maxEndDate.month ? maxEndDate.year : maxEndDate.year - 1;

      return this.format_date_parts(year, month, 1);
    },
    get_forecast_set_end_date_from_start: function (startDateValue) {
      if (!startDateValue) {
        return this.forecast_set_end_date;
      }
      const startDate = new Date(startDateValue);
      let endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + this.forecast_plan_duration_days);

      return this.format_date(endDate);
    },
    get_date_range_days: function (startDateValue, endDateValue) {
      if (!startDateValue || !endDateValue) {
        return 0;
      }

      const startDate = new Date(startDateValue);
      const endDate = new Date(endDateValue);
      return Math.max(this.subtract_dates(endDate, startDate), 0);
    },
    subtract_dates: function (dateOne, dateTwo) {
      const oneDay = 24 * 60 * 60 * 1000;
      const diffInMilliseconds = dateOne.getTime() - dateTwo.getTime();
      return Math.round(diffInMilliseconds / oneDay);
    },
    build_new_forecast_set_data_filters: function () {
      let dataFilters = {};
      for (let level of this.forecast_set_data_levels) {
        const selectedValue = this.new_forecast_set_filters[level];
        if (selectedValue !== undefined && selectedValue !== null && selectedValue !== "") {
          dataFilters[level] = [selectedValue];
        }
      }

      return dataFilters;
    },
    confirm_add_forecast_set: async function () {
      if (!this.can_submit_new_forecast_set) {
        return;
      }

      let forecastSetPayload = {
        start_date: this.new_forecast_set_date_range.start_date,
        end_date: this.new_forecast_set_date_range.end_date,
      };

      if (this.forecast_set_data_levels.length === 1) {
        const selectedLevel = this.forecast_set_data_levels[0];
        forecastSetPayload["channel"] = this.new_forecast_set_filters[selectedLevel];
      } else {
        forecastSetPayload["data_filters"] = this.build_new_forecast_set_data_filters();
      }

      await this.add_forecast_set(forecastSetPayload);

      this.close_add_forecast_set_dialog();
    },
    build_forecast_set_payload: function (forecastSetPayload = {}) {
      return {
        ...this.build_optimization_base_payload(),
        optimization_id: this.my_optimization,
        version_tag: this.selected_version,
        ...forecastSetPayload,
      };
    },
    build_optimization_base_payload: function () {
      return {
        project_id: this.$project.get_val(),
        project_plan_id: this.project_plan_id.toString(),
      };
    },
    add_forecast_set: async function (forecastSetPayload = {}) {
      this.loading = true;
      let token = await this.$auth.getTokenSilently();
      let payload = this.build_forecast_set_payload(forecastSetPayload);
      let result = await api_caller
        .add_forecast_set(token, payload)
        .catch(() => {
          return false;
        });

      if (result && result["success"]) {
        this.assign_optimization(result["data"]);
        return result["data"];
      }

      this.loading = false;
      if (result && result["message"]) {
        console.warn("[OptimizationSetup Error Source]", {
          method: "add_forecast_set",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          channel: forecastSetPayload["channel"],
          data_filters: forecastSetPayload["data_filters"],
          success: result["success"],
          message: result["message"],
          has_data: Boolean(result["data"]),
        });
        this.$emit("triggerAlert", ALERT_TYPE_WARNING, result["message"]);
      } else {
        console.warn("[OptimizationSetup Error Source]", {
          method: "add_forecast_set",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          channel: forecastSetPayload["channel"],
          success: result ? result["success"] : undefined,
          message: result ? result["message"] : undefined,
          has_data: Boolean(result && result["data"]),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
      return false;
    },
    delete_forecast_set: async function (forecastSetPayload = {}) {
      this.loading = true;
      let token = await this.$auth.getTokenSilently();
      let payload = this.build_forecast_set_payload(forecastSetPayload);
      let data = await api_caller
        .delete_forecast_set(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return resp["data"];
        })
        .catch(() => {
          return false;
        });

      if (data) {
        this.assign_optimization(data);
        return data;
      }

      this.loading = false;
      console.warn("[OptimizationSetup Error Source]", {
        method: "delete_forecast_set",
        project_plan_id: this.project_plan_id,
        selected_version: this.selected_version,
        my_optimization: this.my_optimization,
        channel: forecastSetPayload["channel"],
        success: false,
        message: undefined,
        has_data: Boolean(data),
      });
      this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      return false;
    },
    reset_table: async function (resolveModelVersion = true) {
      this.loading = true;
      this.selected_response_curve = [];
      this.selected_response_curve_build = {};
      let token = await this.$auth.getTokenSilently();
      let payload = this.build_optimization_base_payload();
      let data = await api_caller
        .get_optimization_info(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return this.normalize_optimization_info(resp["data"]);
        })
        .catch(() => {
          return false;
        });
      if (data) {
        this.response_curves = data["response_curves"] || {};

        let temp_version = [];
        for (const x of data["versions"]) {
          temp_version.push({ version_value: x, version_label: "version " + x });
        }

        this.available_optimization_versions = temp_version;
        this.selected_version = data["versions"][0];
        if (resolveModelVersion) {
          this.resolve_optimization_model_version(data);
        }

        this.assign_optimization(data);
      } else {
        this.loading = false;
        console.warn("[OptimizationSetup Error Source]", {
          method: "reset_table",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          success: false,
          message: undefined,
          has_data: Boolean(data),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    normalize_optimization_info: function (data) {
      if (!data || typeof data !== "object") {
        return false;
      }

      const versions = this.get_optimization_versions(data);
      if (versions.length === 0) {
        return false;
      }

      return {
        ...data,
        versions,
        optimizations: Array.isArray(data["optimizations"])
          ? data["optimizations"]
          : [],
        setups: Array.isArray(data["setups"]) ? data["setups"] : [],
        response_curves:
          data["response_curves"] && typeof data["response_curves"] === "object"
            ? data["response_curves"]
            : data["response_curve"] && typeof data["response_curve"] === "object"
            ? data["response_curve"]
            : {},
      };
    },
    get_optimization_versions: function (data) {
      if (Array.isArray(data["versions"])) {
        return data["versions"];
      }

      if (Array.isArray(data["available_versions"])) {
        return data["available_versions"];
      }

      const versionSet = new Set();
      const collectVersion = (value) => {
        if (value !== undefined && value !== null && value !== "") {
          versionSet.add(value);
        }
      };

      for (let setup of data["setups"] || []) {
        collectVersion(setup["version"]);
        collectVersion(setup["version_id"]);
        for (let optimization of setup["optimization"] || []) {
          collectVersion(optimization["version_id"]);
        }
      }

      for (let optimization of data["optimizations"] || []) {
        collectVersion(optimization["Version"]);
        collectVersion(optimization["version"]);
        collectVersion(optimization["version_id"]);
      }

      return Array.from(versionSet);
    },
    resolve_optimization_model_version: function (data) {
      if (!data) {
        return;
      }

      const optimization = (data["optimizations"] || []).find((item) => {
        return item &&
          item["Version"] == this.selected_version &&
          this.is_supported_optimization_model_version(item["ModelVersion"]);
      });
      if (!optimization) {
        return;
      }

      this.$emit("optimizationModelVersionResolved", optimization["ModelVersion"]);
    },
    is_supported_optimization_model_version: function (modelVersion) {
      return ["v1", "v2"].includes(modelVersion);
    },
    assign_optimization: function (data) {
      if (data) {
        this.performanceData = data;
        let opt_setups = (this.performanceData["setups"] || []).filter((setup) => {
          return setup["version"] == this.selected_version;
        });
        this.optimization_setups = {};
        for (let setup of opt_setups) {
          this.optimization_setups[setup["id"]] = setup;
        }

        this.optimizations = (this.performanceData["optimizations"] || []).filter((item) => {
          return item && item["ID"] !== undefined && item["Version"] == this.selected_version;
        });

        if(this.visible_optimizations.length > 0){
          this.my_optimization = null;
          setTimeout(() => (this.my_optimization = this.visible_optimizations[0]["ID"]), 100);
        } else {
          this.my_optimization = null;
        }

        this.loading = false;
      } else {
        this.performanceData = null;
        this.loading = true;
      }
    },
    save_optimization: async function (reload_data = true) {
      let optimizations = [];
      let save_ids = new Set(this.save_optimization_ids);
      save_ids = Array.from(save_ids);
      for (let id of Object.keys(this.optimization_setups)) {
        if (save_ids.includes(+id)) {
          let tempOpt = this.optimization_setups[id];
          tempOpt["optimization"] = tempOpt["optimization"].filter((opt) => {
            return opt["version_id"] == this.selected_version;
          });
          optimizations.push(tempOpt);
        }
      }
      this.save_optimization_ids = [];
      this.loading = true;
      let token = await this.$auth.getTokenSilently();
      let payload = {
        project_id: this.$project.get_val(),
        project_plan_id: this.project_plan_id.toString(),
        forecast_title: this.my_optimization_name,
        optimizations: optimizations,
      };
      let data = await api_caller
        .update_optimization(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return resp["data"];
        })
        .catch(() => {
          return false;
        });
      if (data) {
        this.save_optimizations = false;
        if (reload_data) {
          this.assign_optimization(data);
          this.loading = false;
        }
        return data;
      } else {
        console.warn("[OptimizationSetup Error Source]", {
          method: "save_optimization",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          success: false,
          message: undefined,
          has_data: Boolean(data),
          save_optimization_ids: save_ids,
          payload_optimization_count: optimizations.length,
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    add_new_optimization: async function () {
      this.loading = true;
      if (this.save_optimizations) {
        await this.save_optimization(false);
      }
      let token = await this.$auth.getTokenSilently();
      let payload = {
        ...this.build_optimization_base_payload(),
        optimization_title: this.new_optimization_title,
        optimization_risk: this.new_optimization_risk,
        version_tag: this.selected_version,
      };
      let data = await api_caller
        .add_new_optimization(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return resp["data"];
        })
        .catch(() => {
          return false;
        });
      if (data) {
        this.selected_version = this.max_available_version
        this.assign_optimization(data);
        this.new_optimization_title = "";
        this.new_optimization_risk = "low";
      } else {
        console.warn("[OptimizationSetup Error Source]", {
          method: "add_new_optimization",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          success: false,
          message: undefined,
          has_data: Boolean(data),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    copy_optimization: async function () {
      this.loading = true;
      if (this.save_optimizations) {
        await this.save_optimization(false);
      }
      let token = await this.$auth.getTokenSilently();
      let payload = {
        ...this.build_optimization_base_payload(),
        optimization_id: this.my_optimization,
        version_tag: this.selected_version,
      };
      let data = await api_caller
        .copy_optimization(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return resp["data"];
        })
        .catch(() => {
          return false;
        });
      if (data) {
        this.selected_version = this.max_available_version
        this.assign_optimization(data);
      } else {
        console.warn("[OptimizationSetup Error Source]", {
          method: "copy_optimization",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          success: false,
          message: undefined,
          has_data: Boolean(data),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    delete_optimization: async function () {
      this.loading = true;
      if (this.save_optimizations) {
        await this.save_optimization(false);
      }
      let token = await this.$auth.getTokenSilently();
      let payload = {
        ...this.build_optimization_base_payload(),
        optimization_id: this.my_optimization,
        version_tag: this.selected_version,
      };
      let data = await api_caller
        .delete_optimization(token, payload)
        .then((resp) => {
          if (!resp["success"]) {
            return false;
          }
          return resp["data"];
        })
        .catch(() => {
          return false;
        });
      if (data) {
        this.assign_optimization(data);
      } else {
        console.warn("[OptimizationSetup Error Source]", {
          method: "delete_optimization",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          success: false,
          message: undefined,
          has_data: Boolean(data),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    switch_optimization_version: function () {
      this.resolve_optimization_model_version(this.performanceData);
      this.assign_optimization(this.performanceData);
    },
    spend_shifted: function (channel) {
      if (Object.keys(this.selected_response_curve_build).includes(channel)) {
        this.close_response_curve(channel);
        this.open_response_curve(channel);
      }
      this.total_budget_edit_snapshot = null;
      this.sync_total_budget_from_channels();
      this.save_optimization_ids.push(this.my_optimization);
      this.save_optimizations = true;
    },
    open_response_curve: function (channel) {
      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      let channel_spend = 0;
      for (let opt of optSetup["optimization"]) {
        if (opt["channel"] == channel) {
          channel_spend = opt["spend"];
        }
      }

      let cur_channel_length = Object.keys(this.selected_response_curve_build).length;

      let new_resp_curve = [];
      let cur_idx = 0;
      let curve_spend = 0;
      let curve_vol = 0;
      let continue_running = false;

      let tmp_row = [];
      if (
        !this.response_curves ||
        !Object.keys(this.response_curves).includes(channel) ||
        this.response_curves[channel] == null
      ) {
        console.warn("[OptimizationSetup Error Source]", {
          method: "open_response_curve",
          project_plan_id: this.project_plan_id,
          selected_version: this.selected_version,
          my_optimization: this.my_optimization,
          channel: channel,
          success: false,
          message: "Missing response curve for channel",
          has_data: false,
          response_curve_keys: Object.keys(this.response_curves || {}),
        });
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }
      for (let data_row of this.response_curves[channel]) {
        tmp_row = [];
        if (cur_idx == 0) {
          tmp_row.push(data_row[0]);
          for (let fill = 1; fill < 1 + cur_channel_length * 2; fill += 2) {
            tmp_row.push(this.selected_response_curve[0][fill]);
            tmp_row.push("Point");
          }
          tmp_row.push(channel);
          tmp_row.push("Point " + channel);
          new_resp_curve.push(tmp_row);
        } else {
          tmp_row.push(data_row[0]);
          for (let fill = 0; fill < cur_channel_length; fill++) {
            tmp_row.push(null);
            tmp_row.push(null);
          }
          tmp_row.push(data_row[1]);
          tmp_row.push(null);
          new_resp_curve.push(tmp_row);

          if (data_row[0] > channel_spend && !continue_running) {
            if (
              Math.abs(data_row[0] - channel_spend) >
              Math.abs(this.response_curves[channel][cur_idx][0] - channel_spend)
            ) {
              curve_spend = this.response_curves[channel][cur_idx][0];
              curve_vol = this.response_curves[channel][cur_idx][1];
            } else {
              curve_spend = this.response_curves[channel][cur_idx - 1][0];
              curve_vol = this.response_curves[channel][cur_idx - 1][1];
            }
            continue_running = true;
          }
        }
        cur_idx += 1;
      }

      tmp_row = [curve_spend];
      for (let fill = 0; fill < cur_channel_length; fill++) {
        tmp_row.push(null);
        tmp_row.push(null);
      }
      tmp_row.push(null);
      tmp_row.push(0);
      new_resp_curve.push(tmp_row);

      tmp_row = [curve_spend];
      for (let fill = 0; fill < cur_channel_length; fill++) {
        tmp_row.push(null);
        tmp_row.push(null);
      }
      tmp_row.push(null);
      tmp_row.push(curve_vol);
      new_resp_curve.push(tmp_row);

      cur_idx = 0;
      for (let row of this.selected_response_curve) {
        if (cur_idx == 0) {
          cur_idx += 1;
          continue;
        } else {
          cur_idx += 1;
          row.push(null);
          row.push(null);
          new_resp_curve.push(row);
        }
      }

      this.selected_response_curve_build[channel] = [
        1 + cur_channel_length * 2,
        2 + cur_channel_length * 2,
      ];

      this.selected_response_curve = new_resp_curve;
      this.response_curve_optimization = true;
      this.selected_response_curve_channel = channel;
      this.selected_response_curve_spend = curve_spend;
      this.selected_response_curve_volume = curve_vol;
      this.selected_response_curve_roas = curve_vol / curve_spend;
    },
    close_response_curve: function (channel) {
      let new_resp_curve = [];
      let remove_idx = this.selected_response_curve_build[channel];

      let tmp_row = [];
      let all_null = false;
      for (let row of this.selected_response_curve) {
        tmp_row = [];
        all_null = true;

        for (let jdx = 0; jdx < row.length; jdx++) {
          if (!remove_idx.includes(jdx)) {
            tmp_row.push(row[jdx]);
            if (jdx > 0 && row[jdx] != null) {
              all_null = false;
            }
          }
        }

        if (!all_null) {
          new_resp_curve.push(tmp_row);
        }
      }

      delete this.selected_response_curve_build[channel];

      for (let channel of Object.keys(this.selected_response_curve_build)) {
        if (this.selected_response_curve_build[channel][0] > remove_idx[0]) {
          this.selected_response_curve_build[channel][0] -= 2;
          this.selected_response_curve_build[channel][1] -= 2;
        }
      }

      this.selected_response_curve = new_resp_curve;
    },
    handleResponseCurvePointSelect: function (selection) {
      if (!selection || !selection.channel) {
        return;
      }

      let selected_channel = selection.channel;
      let selected_spend = selection.spend;
      let selected_volume = selection.volume;

      this.update_selected_channel_spend(selected_channel, selected_spend);

      this.selected_response_curve_channel = selected_channel;
      this.selected_response_curve_spend = selected_spend;
      this.selected_response_curve_volume = selected_volume;
      this.selected_response_curve_roas = selected_volume / selected_spend;
      this.total_budget_edit_snapshot = null;
      this.sync_total_budget_from_channels();
      this.save_optimizations = true;
      this.save_optimization_ids.push(this.my_optimization);
    },
    update_selected_channel_spend: function (selected_channel, selected_spend) {
      for (let idx = 0; idx < this.optimization_channels.length; idx++) {
        if (this.optimization_channels[idx]["channel"] == selected_channel) {
          this.$set(this.optimization_channels[idx], "spend", selected_spend);
          break;
        }
      }

      const optSetup = this.get_current_optimization_setup();
      if (!optSetup) {
        return;
      }

      for (let opt of optSetup["optimization"]) {
        if (opt["channel"] == selected_channel) {
          this.$set(opt, "spend", selected_spend);
          break;
        }
      }
    },
  },
};
</script>

<style scoped>
.optimization_response_curve_title {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 1.1em;
}
/* .data_breakdown_chip {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 0.85em;
  width: 150px;
} */
.optimization-tabs-column {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.optimization-tabs-rail {
  height: 58vh;
}
.optimization-setup-container,
.optimization-loading-page {
  height: 100%;
}
.optimization-setup-container {
  max-width: 100%;
}
.optimization-loading-page {
  min-height: calc(100vh - 230px);
  width: 100%;
}
.optimization-setup-column {
  height: 58vh;
  min-height: 0;
}
.optimization-graph-column {
  height: 58vh;
  min-height: 0;
}
.optimization-setup-panel {
  height: 100%;
  overflow-y: auto;
  width: 100%;
}
.optimization-graph-panel {
  height: 100%;
  width: 100%;
}
.optimization-channel-card,
.optimization-response-chart-row {
  width: 100%;
}
.optimization-channel-card-layout {
  align-items: stretch;
  display: flex;
  gap: 8px;
  min-height: 104px;
}
.optimization-channel-card-fields {
  align-self: center;
  flex: 1 1 auto;
  min-width: 0;
}
.optimization-channel-card-actions {
  align-items: center;
  display: flex;
  flex: 0 0 28px;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px 0;
}
.optimization-channel-actions-row {
  min-height: 28px;
}
.optimization-add-forecast-set-row {
  min-height: 36px;
}
.optimization-channel-remove-dialog-text {
  font-family: "Poppins" !important;
  font-size: 0.95em;
}
.optimization-forecast-set-dialog ::v-deep .v-card__title,
.optimization-forecast-set-dialog ::v-deep .v-card__text,
.optimization-forecast-set-dialog ::v-deep .v-btn,
.optimization-forecast-set-dialog ::v-deep .v-btn__content {
  font-family: "Poppins" !important;
}
.optimization-forecast-set-channel-section {
  margin-bottom: 14px !important;
}
.optimization-forecast-set-actions {
  justify-content: center;
}
.optimization-forecast-set-add-button {
  min-width: 180px !important;
}
.optimization-forecast-set-form {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-top: 4px;
}
.optimization-date-picker-row {
  margin-top: 0;
}
.optimization-scheduler-label {
  color: rgba(0, 0, 0, 0.6);
  font-family: "Poppins" !important;
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
}
.optimization-forecast-month-select ::v-deep .v-select__selection,
.optimization-forecast-month-select ::v-deep input {
  font-size: 1.05rem;
}
.optimization-date-range-summary {
  margin-top: 32px;
}
.optimization-date-range-summary__panel {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px 18px;
}
.optimization-date-range-summary__item {
  min-width: 0;
}
.optimization-date-range-summary__label {
  color: rgba(0, 0, 0, 0.55);
  font-family: "Poppins" !important;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
}
.optimization-date-range-summary__value {
  color: rgba(0, 0, 0, 0.86);
  font-family: "Poppins" !important;
  font-size: 1.08rem;
  font-weight: 600;
}
.optimization-channel-remove-dialog ::v-deep .v-card__title,
.optimization-channel-remove-dialog ::v-deep .v-card__text,
.optimization-channel-remove-dialog ::v-deep .v-btn,
.optimization-channel-remove-dialog ::v-deep .v-btn__content {
  font-family: "Poppins" !important;
}
.optimization-response-chart-row,
.optimization-response-chart-column {
  height: 100%;
}
@media (min-width: 960px) {
  .optimization-setup-layout {
    height: 100%;
  }
  .optimization-setup-layout {
    position: relative;
  }
  .optimization-setup-column {
    height: 100%;
  }
  .optimization-graph-column {
    height: 100%;
  }
  .optimization-setup-layout::before,
  .optimization-setup-layout::after {
    background: #e0e0e0;
    bottom: 0;
    content: "";
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 1px;
    z-index: 1;
  }
  .optimization-setup-layout::before {
    left: 8.333333%;
  }
  .optimization-setup-layout::after {
    left: 33.333333%;
  }
  .optimization-setup-panel {
    margin-left: 16px !important;
    margin-right: 16px !important;
    width: calc(100% - 32px);
  }
}
.optimization-rail-section {
  margin-bottom: 24px;
}
.optimization-rail-label {
  color: #757575;
  display: block;
  font-family: "Poppins" !important;
  font-size: 0.72em;
  font-weight: 500;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
  text-transform: uppercase;
}
.optimization-version-select {
  font-family: "Poppins" !important;
  font-size: 0.85em;
  margin: 0;
  padding: 0;
}
.optimization-version-warning {
  background: #fff8e8;
  border: 1px solid #ead9aa;
  border-radius: 4px;
  color: #604a0f;
  font-family: "Poppins" !important;
  font-size: 0.7em;
  line-height: 1.35;
  margin-top: 8px;
  padding: 6px 8px;
}
.optimization-version-tabs {
  width: 100%;
  flex: 0 0 auto;
}
.optimization-version-tabs ::v-deep .v-slide-group,
.optimization-version-tabs ::v-deep .v-slide-group__wrapper {
  width: 100%;
}
.optimization-version-tabs ::v-deep .v-slide-group__content {
  width: 100%;
}
.optimization-version-tabs ::v-deep .v-tabs-bar {
  background: transparent;
  height: auto;
  width: 100%;
}
.optimization-version-tabs ::v-deep .v-tabs-slider-wrapper {
  display: none;
}
.optimization-version-tab {
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  font-family: "Poppins" !important;
  font-size: 0.85em;
  font-weight: 500;
  justify-content: flex-start;
  letter-spacing: 0;
  margin-bottom: 8px;
  min-height: 36px;
  max-width: none;
  min-width: 100%;
  padding: 0 10px;
  text-transform: none;
  width: 100%;
}
.optimization-version-tab.v-tab--active {
  border-color: #000000;
  color: #000000;
  font-weight: 600;
}
.optimization-version-tab::before {
  border-radius: 4px;
}
.optimization-tab-actions {
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  margin-top: 4px;
  padding-top: 8px;
  width: 100%;
}
.optimization-tab-action {
  display: flex;
  flex: 1 1 0;
  justify-content: center;
  min-width: 0;
}
.optimization-total-budget-row {
  width: 100%;
}
.optimization-total-budget-field {
  min-width: 0;
  width: 100%;
}
.optimization-total-budget-toggle-row {
  align-items: flex-end;
  display: flex;
  gap: 8px;
  width: 100%;
}
.optimization-total-budget-toggle {
  border: 1px solid #000000;
  border-radius: 4px;
  box-shadow: none;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}
.optimization-total-budget-toggle ::v-deep .v-btn {
  border-radius: 0;
}
.optimization-total-budget-toggle-tooltip {
  background: #ffffff;
  display: flex;
  flex: 1 1 0;
  min-width: 0;
}
.optimization-total-budget-toggle-tooltip--active {
  background: #000000;
}
.optimization-total-budget-toggle-button {
  background: #ffffff !important;
  border: 0 !important;
  border-left: 1px solid #000000 !important;
  color: #000000 !important;
  font-family: "Poppins" !important;
  font-size: 0.72em;
  font-weight: 400;
  height: 30px !important;
  letter-spacing: 0;
  min-width: 0;
  padding: 0 8px;
  text-transform: none;
  width: 100%;
}
.optimization-total-budget-toggle-tooltip:first-child .optimization-total-budget-toggle-button {
  border-left: 0 !important;
}
.optimization-total-budget-toggle-tooltip--active .optimization-total-budget-toggle-button,
.optimization-total-budget-toggle-button.v-btn--active {
  background: #000000 !important;
  color: #ffffff !important;
}
.optimization-total-budget-toggle-button.v-btn--active::before,
.optimization-total-budget-toggle-button::before {
  opacity: 0 !important;
}
.optimization-total-budget-toggle-button.v-btn--disabled,
.optimization-total-budget-toggle-button.v-btn--active.v-btn--disabled {
  background: #f5f5f5 !important;
  border-color: rgba(0, 0, 0, 0.26) !important;
  color: rgba(0, 0, 0, 0.26) !important;
}
.optimization-total-budget-toggle.v-item-group--disabled .optimization-total-budget-toggle-tooltip {
  background: #f5f5f5;
}
.optimization-total-budget-toggle.v-item-group--disabled {
  border-color: rgba(0, 0, 0, 0.26);
}
.optimization-total-budget-reset {
  flex: 0 0 auto;
}
@media (max-width: 1260px) {
  .optimization-total-budget-reset {
    align-self: flex-end;
  }
}
.planning_section_action_text {
  font-family: "Poppins" !important;
  font-weight: 600;
}
.optimization_setup_title {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 0.85em;
}
.optimization_create_title {
  font-family: "Poppins" !important;
  font-weight: 600;
}
.optimization_calculator_channel {
  font-family: "Poppins" !important;
  font-weight: 600;
}
.optimization_calculator_spend {
  font-family: "Poppins" !important;
  font-weight: 400;
}
.optimization_calculator_action {
  font-family: "Poppins" !important;
  font-weight: 400;
}
</style>
